import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackendClient } from '@/infrastructure/clients/BackendClient';
import { captureError } from '@/utils/sentry';
import LureoButton from '@/common/LureoButton';
import lureoBetaLogo from '../../assets/lureo_beta_logo_color.png';
import { ThemedLogo } from '@/modules/theme';
import { Input } from '@/components/cms/ui/input';
import { FlowTitle } from '@/components/ui/text';
import { PageFrame } from '@/components/ui/page-frame';

const panelStyle: string = 'flex flex-col items-center space-y-2';

// 'checking' until we know whether a usable recovery session exists; 'ready' once
// it does (form enabled); 'invalid' when the link carried no usable session, so we
// never let the user type a new password into a dead form (the old flow only
// surfaced that at submit time as a confusing "Unable to retrieve email").
type LinkState = 'checking' | 'ready' | 'invalid';

function readRecoveryTokensFromHash(): { accessToken: string; refreshToken: string } | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  if (params.get('type') !== 'recovery') return null;
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

export const PasswordResetConfirm = () => {
  const [password, setPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [linkState, setLinkState] = useState<LinkState>('checking');
  // Read once the link is usable, so a submit never depends on the stored session it replaces mid-way
  const [email, setEmail] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const tokens = readRecoveryTokensFromHash();
      try {
        if (tokens) {
          const { data } = await BackendClient.auth.setSession({
            access_token: tokens.accessToken,
            refresh_token: tokens.refreshToken,
          });
          if (cancelled) return;
          // Strip the tokens from the URL so they aren't left in history.
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          if (data?.session?.user?.email) {
            setEmail(data.session.user.email);
            setLinkState('ready');
            return;
          }
          // Tokens were present but didn't resolve to a session — expired/used link.
          captureError(new Error('Password reset: recovery tokens did not resolve a session'), {
            domain: 'auth',
            operation: 'passwordResetRestoreSession',
          });
        } else {
          // No implicit-flow recovery fragment in the URL. The common cause is the
          // email link not delivering one — e.g. a PKCE `?code=` link (not yet
          // handled here) or a redirect_to that isn't in Supabase's allowlist, which
          // makes GoTrue drop the tokens. Fall back to any existing session; if there
          // is none the link is unusable. The context flags below let Sentry tell the
          // two causes apart instead of leaving this failure invisible.
          const { data } = await BackendClient.auth.getSession();
          if (cancelled) return;
          if (data?.session?.user?.email) {
            setEmail(data.session.user.email);
            setLinkState('ready');
            return;
          }
          captureError(new Error('Password reset: opened without a recovery session'), {
            domain: 'auth',
            operation: 'passwordResetNoSession',
            hasHash: typeof window !== 'undefined' && window.location.hash.length > 1,
            hasQueryCode: typeof window !== 'undefined'
              && new URLSearchParams(window.location.search).has('code'),
          });
        }
      } catch (err) {
        if (!cancelled) {
          captureError(err, { domain: 'auth', operation: 'passwordResetRestoreSession' });
        }
      }
      if (!cancelled) {
        setLinkState('invalid');
        setStatusMessage('This password-reset link is invalid or has expired. Please request a new one.');
        setTimeout(() => navigate('/login'), 3000);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    if (password.length < 6) {
      setStatusMessage('Password must be at least 6 characters long');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    // Released only when the person can act on this page again; a success leaves it for another page
    const release = () => {
      submittingRef.current = false;
      setSubmitting(false);
    };

    try {
      if (!email) {
        setStatusMessage('Your reset session has expired. Please request a new link.');
        captureError(new Error('Password reset: session missing at submit'), {
          domain: 'auth',
          operation: 'passwordResetSubmit',
        });
        await BackendClient.auth.signOut();
        setTimeout(() => navigate('/login'), 2500);
        return;
      }

      const { error } = await BackendClient.auth.updateUser({
        password: password
      });

      if (error) {
        setStatusMessage(error.message);
        captureError(new Error(`Password reset: updateUser failed: ${error.message}`), {
          domain: 'auth',
          operation: 'passwordResetUpdateUser',
        });
        release();
        return;
      }

      setStatusMessage('Password updated successfully');

      try {
        await BackendClient.auth.signOut();
        const { error: signInError } = await BackendClient.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) {
          setStatusMessage('Password updated but login failed. Please try logging in manually.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setTimeout(() => navigate('/dashboard'), 1000);
        }
      } catch (authError) {
        captureError(authError, { domain: 'auth', operation: 'passwordResetReSignIn' });
        setStatusMessage('Password updated but login failed. Please try logging in manually.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      captureError(error, { domain: 'auth', operation: 'passwordResetSubmit' });
      setStatusMessage('An error occurred. Please try again.');
      release();
    }
  };

  const formDisabled = linkState !== 'ready' || submitting;

  return (
    <PageFrame layout="centred" width="sm" surface="own" className="bg-lureo-emphasis-strong">
      <div className="flex w-full max-w-sm flex-col items-center">
        <span className="mb-8 w-full max-w-72 sm:max-w-80">
          <ThemedLogo src={lureoBetaLogo} alt="Lureo Logo" className="h-auto w-full" />
        </span>
        <div className="flex w-full flex-col rounded-lureo-radius bg-lureo-backdrop px-4 pb-3 pt-2 shadow-lureo-shadow-sheet">
          <div className="mb-2 flex text-center">
            <FlowTitle as="h1" className="w-full px-4 py-2">
              Reset Password
            </FlowTitle>
          </div>
          <div className="flex flex-col">
            <form onSubmit={handleSetNewPassword} className="flex flex-col">
              <div className={panelStyle}>
                <Input
                  look="outlined"
                  type="password"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={formDisabled}
                />
                <div className="h-4" />
              </div>
              <LureoButton
                type="submit"
                variant="secondary"
                className="h-10 w-full rounded-lureo-radius-button-pill"
                disabled={formDisabled}
              >
                {linkState === 'checking' ? 'Preparing reset…' : submitting ? 'Updating...' : 'Update Password'}
              </LureoButton>
            </form>
            {statusMessage && (
              <p className="mt-2 text-center text-lureo-14 text-lureo-sky-ink leading-5">
                {statusMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
};
